import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Trash, Printer, CheckCircle, Circle, Bluetooth, FloppyDisk,
    PaperPlaneRight, Bell, Scissors, ArrowsClockwise, Receipt, ChefHat,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PageHeader from "@/admin/components/PageHeader";
import {
    pairPrinter, disconnectPrinter, printTestPage,
    subscribePrinterState, getState as getBtState,
    loadProfiles, saveProfiles, loadRoles, saveRoles, getProfile,
} from "@/lib/btThermal";

const PRESETS = [
    {
        key: "goojprt-58",
        label: "GOOJPRT / 58mm genérica",
        config: { width: 58, chars: 32, codepage: 0, cutMode: "partial", beepOnPrint: true, density: 0, feedAfter: 4 },
    },
    {
        key: "goojprt-80",
        label: "GOOJPRT / 80mm genérica",
        config: { width: 80, chars: 42, codepage: 0, cutMode: "partial", beepOnPrint: true, density: 0, feedAfter: 4 },
    },
    {
        key: "epson",
        label: "Epson / Star (ESC/POS clásico)",
        config: { width: 80, chars: 42, codepage: 19, cutMode: "partial", beepOnPrint: false, density: 0, feedAfter: 5 },
    },
    {
        key: "munbyn",
        label: "MUNBYN / Phomemo / Rongta 58mm",
        config: { width: 58, chars: 32, codepage: 16, cutMode: "none", beepOnPrint: false, density: 1, feedAfter: 5 },
    },
    {
        key: "xprinter",
        label: "Xprinter XP-58 / XP-P200",
        config: { width: 58, chars: 32, codepage: 0, cutMode: "partial", beepOnPrint: false, density: 0, feedAfter: 4 },
    },
];

const CODEPAGES = [
    { v: 0, n: "CP437 (default GOOJPRT)" },
    { v: 2, n: "CP850 (multilingüe)" },
    { v: 16, n: "Windows-1252 (Epson)" },
    { v: 19, n: "CP858 (con €)" },
];

const CUTS = [
    { v: "partial", n: "Corte parcial (cutter)" },
    { v: "none",    n: "Solo avanzar papel" },
];

function uid() {
    return "p_" + Math.random().toString(36).slice(2, 9);
}

export default function PrinterSettings() {
    const [profiles, setProfiles] = useState(loadProfiles());
    const [roles, setRoles] = useState(loadRoles());
    const [btState, setBtState] = useState(getBtState());
    const [busy, setBusy] = useState(false);

    useEffect(() => subscribePrinterState(setBtState), []);

    const persist = (next, nextRoles = roles) => {
        setProfiles(next);
        saveProfiles(next);
        setRoles(nextRoles);
        saveRoles(nextRoles);
    };

    const updateProfile = (id, patch) => {
        const next = profiles.map((p) => (p.id === id ? { ...p, ...patch } : p));
        persist(next);
    };

    const addProfile = (presetKey) => {
        const preset = PRESETS.find((p) => p.key === presetKey) || PRESETS[0];
        const newProfile = {
            id: uid(),
            name: preset.label,
            ...preset.config,
        };
        persist([...profiles, newProfile]);
        toast.success(`Perfil "${preset.label}" agregado`);
    };

    const removeProfile = (id) => {
        if (profiles.length <= 1) {
            toast.error("Debe quedar al menos un perfil");
            return;
        }
        if (!window.confirm("¿Eliminar este perfil?")) return;
        const next = profiles.filter((p) => p.id !== id);
        // Reset any role pointing to the deleted profile.
        const fallback = next[0].id;
        const nextRoles = {
            customer: roles.customer === id ? fallback : roles.customer,
            kitchen: roles.kitchen === id ? fallback : roles.kitchen,
        };
        persist(next, nextRoles);
    };

    const handlePair = async () => {
        setBusy(true);
        try {
            const r = await pairPrinter();
            toast.success(`Conectada: ${r.name}`);
        } catch (e) {
            if (e?.name !== "NotFoundError") {
                toast.error(e.message || "No se pudo conectar");
            }
        } finally {
            setBusy(false);
        }
    };

    const handleTest = async (profile) => {
        if (!btState.connected) {
            toast.error("Primero conecta una impresora Bluetooth");
            return;
        }
        setBusy(true);
        try {
            await printTestPage(profile);
            toast.success("Página de prueba enviada");
        } catch (e) {
            toast.error(e.message || "Falló la impresión");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="printer-settings">
            <PageHeader kicker="Hardware" title="Ajustes de impresora" />

            {/* Bluetooth connection card */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-blue-500/10 text-blue-600">
                        <Bluetooth size={20} weight="fill" />
                    </span>
                    <div>
                        <h3 className="font-display font-black text-xl">Bluetooth</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Vincula tu impresora portátil
                        </p>
                    </div>
                </div>

                {btState.supported ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <div
                            data-testid="bt-status-card"
                            className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 ${
                                btState.connected
                                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                                    : "border-border bg-white text-foreground/60"
                            }`}
                        >
                            <span className={`h-2 w-2 rounded-full ${btState.connected ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
                            {btState.connected ? `Conectada · ${btState.name}` : "Sin impresora"}
                        </div>
                        {!btState.connected ? (
                            <Button
                                data-testid="bt-pair-btn"
                                disabled={busy}
                                onClick={handlePair}
                                className="rounded-full bg-foreground text-background hover:opacity-90 font-bold uppercase tracking-widest text-xs"
                            >
                                <Bluetooth size={14} weight="bold" className="mr-2" />
                                Buscar y conectar
                            </Button>
                        ) : (
                            <Button
                                data-testid="bt-disconnect-btn"
                                onClick={() => disconnectPrinter()}
                                variant="outline"
                                className="rounded-full text-xs font-bold uppercase tracking-widest"
                            >
                                Desconectar
                            </Button>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-300 rounded-xl p-3">
                        Tu navegador no soporta Web Bluetooth.
                        Abre esta página en <b>Chrome</b> o <b>Edge</b> (Android, Mac, Windows o Chromebook)
                        para conectar impresoras Bluetooth. En iOS la impresión funciona vía AirPrint del sistema.
                    </p>
                )}
            </Card>

            {/* Role assignments */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-orange-500/10 text-orange-600">
                        <PaperPlaneRight size={20} weight="fill" />
                    </span>
                    <div>
                        <h3 className="font-display font-black text-xl">Asignación por uso</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">
                            Cada tipo de ticket usa su propio perfil
                        </p>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <RoleSelector
                        icon={Receipt}
                        title="Recibo de cliente"
                        value={roles.customer}
                        profiles={profiles}
                        onChange={(v) => { const nr = { ...roles, customer: v }; setRoles(nr); saveRoles(nr); }}
                        testId="role-customer"
                    />
                    <RoleSelector
                        icon={ChefHat}
                        title="Ticket de cocina"
                        value={roles.kitchen}
                        profiles={profiles}
                        onChange={(v) => { const nr = { ...roles, kitchen: v }; setRoles(nr); saveRoles(nr); }}
                        testId="role-kitchen"
                    />
                </div>
            </Card>

            {/* Profiles list */}
            <Card className="p-6 mb-6">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div>
                        <h3 className="font-display font-black text-xl">Perfiles guardados</h3>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                            Cada perfil son ajustes para una impresora · {profiles.length} en total
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            data-testid="profile-preset"
                            id="profile-preset"
                            className="h-9 px-3 rounded-md border border-input bg-background text-xs font-bold"
                            defaultValue="goojprt-58"
                        >
                            {PRESETS.map((p) => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                        <Button
                            data-testid="profile-add"
                            size="sm"
                            onClick={() => {
                                const sel = document.getElementById("profile-preset");
                                addProfile(sel?.value || "goojprt-58");
                            }}
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest text-xs"
                        >
                            <Plus size={12} weight="bold" className="mr-1" /> Nuevo perfil
                        </Button>
                    </div>
                </div>

                <div className="space-y-4">
                    {profiles.map((p) => (
                        <ProfileEditor
                            key={p.id}
                            profile={p}
                            onChange={(patch) => updateProfile(p.id, patch)}
                            onRemove={() => removeProfile(p.id)}
                            onTest={() => handleTest(p)}
                            canDelete={profiles.length > 1}
                            isDefaultCustomer={roles.customer === p.id}
                            isDefaultKitchen={roles.kitchen === p.id}
                            busy={busy}
                            btConnected={btState.connected}
                        />
                    ))}
                </div>
            </Card>

            {/* Help */}
            <Card className="p-6 bg-muted/40">
                <h4 className="font-display font-black text-base mb-2">¿Necesitas ayuda?</h4>
                <ul className="text-sm space-y-1.5 text-muted-foreground">
                    <li>• Carga la impresora y enciéndela. La luz debe parpadear.</li>
                    <li>• Pulsa <b>Buscar y conectar</b> arriba. Selecciona tu impresora en la lista.</li>
                    <li>• Si tu impresora tiene rollo de 80mm, cambia el ancho del perfil a 80 (42 caracteres por línea).</li>
                    <li>• Si los acentos salen raros, prueba con <b>CP850</b> o <b>Windows-1252</b> en &quot;Página de códigos&quot;.</li>
                    <li>• Si la impresora no corta el papel sola, pon &quot;Corte&quot; en <b>Solo avanzar papel</b>.</li>
                    <li>• Usa <b>Imprimir prueba</b> para verificar antes de cobrar.</li>
                </ul>
            </Card>
        </div>
    );
}

function RoleSelector({ icon: Icon, title, value, profiles, onChange, testId }) {
    return (
        <div className="rounded-2xl border border-border p-4 bg-white">
            <div className="flex items-center gap-2 mb-2">
                <Icon size={18} weight="fill" className="text-orange-500" />
                <p className="font-bold text-sm">{title}</p>
            </div>
            <select
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
                {profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
        </div>
    );
}

function ProfileEditor({ profile, onChange, onRemove, onTest, canDelete, isDefaultCustomer, isDefaultKitchen, busy, btConnected }) {
    return (
        <div className="rounded-2xl border-2 border-border p-4 bg-white" data-testid={`profile-${profile.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <Input
                        data-testid={`profile-${profile.id}-name`}
                        value={profile.name}
                        onChange={(e) => onChange({ name: e.target.value })}
                        className="font-display font-black text-lg h-11 border-0 px-0 shadow-none focus-visible:ring-0"
                    />
                    <div className="flex gap-1.5 flex-wrap mt-1">
                        {isDefaultCustomer && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-orange-500/10 text-orange-700 px-2 py-0.5 rounded-full">
                                <Receipt size={10} weight="fill" /> Recibos
                            </span>
                        )}
                        {isDefaultKitchen && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full">
                                <ChefHat size={10} weight="fill" /> Cocina
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1.5">
                    <Button
                        data-testid={`profile-${profile.id}-test`}
                        size="sm"
                        variant="outline"
                        onClick={onTest}
                        disabled={busy || !btConnected}
                        className="rounded-full text-xs font-bold uppercase tracking-widest"
                        title={btConnected ? "Imprimir página de prueba" : "Conecta una impresora primero"}
                    >
                        <Printer size={12} weight="bold" className="mr-1" /> Prueba
                    </Button>
                    {canDelete && (
                        <button
                            data-testid={`profile-${profile.id}-remove`}
                            onClick={onRemove}
                            aria-label="Eliminar perfil"
                            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"
                        >
                            <Trash size={14} weight="bold" />
                        </button>
                    )}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Ancho del rollo">
                    <div className="inline-flex w-full rounded-md border border-input bg-background overflow-hidden">
                        {[58, 80].map((w) => (
                            <button
                                key={w}
                                type="button"
                                data-testid={`profile-${profile.id}-width-${w}`}
                                onClick={() => onChange({ width: w, chars: w === 58 ? 32 : 42 })}
                                className={`flex-1 h-9 text-xs font-bold uppercase tracking-widest ${
                                    profile.width === w
                                        ? "bg-foreground text-background"
                                        : "text-foreground/70 hover:bg-muted"
                                }`}
                            >
                                {w}mm
                            </button>
                        ))}
                    </div>
                </Field>

                <Field label="Caracteres por línea">
                    <Input
                        data-testid={`profile-${profile.id}-chars`}
                        type="number"
                        min={20}
                        max={64}
                        value={profile.chars}
                        onChange={(e) => onChange({ chars: Number(e.target.value) || 32 })}
                        className="h-9"
                    />
                </Field>

                <Field label="Página de códigos">
                    <select
                        data-testid={`profile-${profile.id}-codepage`}
                        value={profile.codepage}
                        onChange={(e) => onChange({ codepage: Number(e.target.value) })}
                        className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs"
                    >
                        {CODEPAGES.map((c) => (
                            <option key={c.v} value={c.v}>{c.n}</option>
                        ))}
                    </select>
                </Field>

                <Field label="Modo de corte">
                    <select
                        data-testid={`profile-${profile.id}-cut`}
                        value={profile.cutMode}
                        onChange={(e) => onChange({ cutMode: e.target.value })}
                        className="w-full h-9 px-2 rounded-md border border-input bg-background text-xs"
                    >
                        {CUTS.map((c) => (
                            <option key={c.v} value={c.v}>{c.n}</option>
                        ))}
                    </select>
                </Field>

                <Field label="Densidad (-3 a +3)">
                    <Input
                        data-testid={`profile-${profile.id}-density`}
                        type="number"
                        min={-3}
                        max={3}
                        value={profile.density}
                        onChange={(e) => onChange({ density: Math.max(-3, Math.min(3, Number(e.target.value) || 0)) })}
                        className="h-9"
                    />
                </Field>

                <Field label="Avance final (líneas)">
                    <Input
                        data-testid={`profile-${profile.id}-feed`}
                        type="number"
                        min={0}
                        max={10}
                        value={profile.feedAfter}
                        onChange={(e) => onChange({ feedAfter: Math.max(0, Math.min(10, Number(e.target.value) || 0)) })}
                        className="h-9"
                    />
                </Field>

                <label className="inline-flex items-center gap-2 cursor-pointer self-end pb-2">
                    <input
                        type="checkbox"
                        data-testid={`profile-${profile.id}-beep`}
                        checked={!!profile.beepOnPrint}
                        onChange={(e) => onChange({ beepOnPrint: e.target.checked })}
                        className="h-4 w-4 accent-primary"
                    />
                    <Bell size={14} weight="bold" />
                    <span className="text-xs font-bold uppercase tracking-widest">Beep al imprimir</span>
                </label>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                {label}
            </Label>
            {children}
        </div>
    );
}
